// @ts-nocheck
/**
 * FollowupsScreen — Tâches (W4, B2-followups).
 *
 * Three tabs in `?tab=` (useTabParam): Agenda (the day's planning and the
 * meetings to handle, Booking merged in), Rappels (the dated callbacks) and
 * Actions IA. An old `?tab=booking` link lands on Agenda.
 *
 * Every read is narrowed to the active customer (ADR-024): the callbacks
 * through useScheduledCallbacks (the same query as the menu badge, ⌘K and the
 * home page), the meetings through `customerFilter`. Marking a callback done
 * or postponing it is reversible: an undo toast, no confirmation (DES-13); a
 * failed write says so and leaves the task as it was.
 *
 * Doc: docs/refonte-ui/audit-ui-ux.md « /ParallelDialer/followups ».
 */
import React, { useState, useMemo } from 'react';
import { HUBSPOT_UI_ENABLED } from '@/lib/hubspotUi';
import { motion, AnimatePresence } from 'framer-motion';
import { useDialer } from '@/contexts/DialerContext';
import { useTabParam } from '@/hooks/useTabParam';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/api-client';
import { formatPhoneNumber } from '@/utils';
import { toast } from 'sonner';
import { currentLocale, formatTime, formatWeekdayDay } from '@/i18n/locale';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import TaskAgendaVF from '@/components/dialer/TaskAgendaVF';
import {
  Calendar,
  ListTodo,
  Sparkles,
  Phone,
  CheckCircle2,
  Clock,
  AlertCircle,
  AlertTriangle,
  User,
  ArrowRight,
  RefreshCw,
  ChevronRight,
  Lightbulb,
  TrendingUp,
  Target,
  Upload,
} from 'lucide-react';
import { useScheduledCallbacks, customerFilter } from '@/hooks/useScheduledCallbacks';
import { countOverdueFollowups } from '@/hooks/useDialerDerivedData';
import { showErrorToast } from '@/lib/errorToast';

// ─── Tab configuration ───────────────────────────────────────────────────────

// labelKey → i18n key in dialer:tasks.*. Resolved at render time via t().
// Booking is merged into Agenda (W4): same icon, same meetings, two tabs.
// The callbacks tab reads « Rappels » so it no longer repeats the page title.
export const TABS = [
  { id: 'agenda',  labelKey: 'agenda',       icon: Calendar },
  { id: 'tasks',   labelKey: 'callbacksTab', icon: ListTodo },
  { id: 'ai',      labelKey: 'aiActions',    icon: Sparkles },
];
const TAB_IDS = TABS.map((tab) => tab.id);

// ─── Date helpers ────────────────────────────────────────────────────────────

function getDateBoundaries() {
  const now = new Date();
  const startOfTomorrow = new Date(now);
  startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);
  startOfTomorrow.setHours(0, 0, 0, 0);

  const endOfToday = new Date(now);
  endOfToday.setHours(23, 59, 59, 999);

  const endOfTomorrow = new Date(startOfTomorrow);
  endOfTomorrow.setHours(23, 59, 59, 999);

  const endOfWeek = new Date(now);
  // End of this calendar week (Sunday)
  endOfWeek.setDate(endOfWeek.getDate() + (7 - endOfWeek.getDay()));
  endOfWeek.setHours(23, 59, 59, 999);

  return { now, endOfToday, startOfTomorrow, endOfTomorrow, endOfWeek };
}


/** The new date of a postponed task, from the menu choice. */
export function rescheduleDate(type, now = new Date()) {
  if (type === 'later_today') {
    const d = new Date(now);
    d.setHours(17, 0, 0, 0);
    if (d < now) { d.setDate(d.getDate() + 1); d.setHours(10, 0, 0, 0); }
    return d;
  }
  if (type === '1d') return new Date(now.getTime() + 86400000);
  if (type === '1w') return new Date(now.getTime() + 7 * 86400000);
  const months = { '1m': 1, '3m': 3, '6m': 6 }[type];
  if (!months) return null;
  const d = new Date(now);
  d.setMonth(d.getMonth() + months);
  return d;
}

/** A meeting's date: the stored field is meeting_date (scheduledAt is not stored, AUDIT-F14). */
function meetingDate(m) {
  return m.meeting_date || m.scheduledAt || null;
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}j`;
}

// ─── Task group section ───────────────────────────────────────────────────────

function TaskSection({ label, icon: Icon, iconClass, headerClass, tasks, onCall, onDone, onReschedule, t }) {
  const [collapsed, setCollapsed] = useState(false);

  if (tasks.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card rounded-xl border border-border overflow-hidden"
    >
      {/* Section header */}
      <button
        onClick={() => setCollapsed(c => !c)}
        className={`w-full px-4 py-2.5 border-b border-border/50 flex items-center justify-between ${headerClass}`}
      >
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${iconClass}`} />
          <span className="text-sm font-semibold text-foreground">{label}</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-muted text-foreground/70 border-0">{tasks.length}</Badge>
          <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${collapsed ? '' : 'rotate-90'}`} />
        </div>
      </button>

      {/* Task rows */}
      <AnimatePresence>
        {!collapsed && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            className="overflow-hidden divide-y divide-border/50"
          >
            {tasks.map(task => (
              <TaskRow
                key={task.id}
                task={task}
                onCall={onCall}
                onDone={onDone}
                onReschedule={onReschedule}
                t={t}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ─── Individual task row ──────────────────────────────────────────────────────

function TaskRow({ task, onCall, onDone, onReschedule, t }) {
  const isOverdue = new Date(task.follow_up_at) < new Date();
  const type = task.call_outcome === 'callback_requested' ? t('dialer:tasks.taskType.callback') : t('dialer:tasks.taskType.followUp');

  return (
    <div className="px-4 py-3 hover:bg-accent/30 transition-colors">
      <div className="flex items-start justify-between gap-3">
        {/* Left: prospect info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <User className="w-3.5 h-3.5 text-muted-foreground/50 flex-shrink-0" />
            <span className="font-medium text-foreground text-sm truncate">{task.prospect_name}</span>
            {task.prospect_company && (
              <span className="text-xs text-muted-foreground/60 truncate hidden sm:inline">
                , {task.prospect_company}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="font-mono">{formatPhoneNumber(task.prospect_phone)}</span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {isOverdue
                ? t('dialer:tasks.summary.overdueBy', { time: timeAgo(task.follow_up_at) })
                : `${formatWeekdayDay(task.follow_up_at)} ${formatTime(task.follow_up_at)}`}
            </span>
            <Badge variant="outline" className="text-[10px] h-4 px-1.5 border-border/50">{type}</Badge>
          </div>
          {task.call_notes && (
            <p className="text-xs text-muted-foreground/70 mt-1.5 bg-muted/40 px-2 py-1 rounded line-clamp-1">
              {task.call_notes}
            </p>
          )}
        </div>

        {/* Right: actions */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <Select onValueChange={(v) => onReschedule(task, v)}>
            <SelectTrigger className="h-7 w-[4.5rem] text-xs border-border/60" aria-label={t('dialer:tasks.reschedule.placeholder')}>
              <SelectValue placeholder={t('dialer:tasks.reschedule.placeholder')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="later_today">{t('dialer:tasks.reschedule.later')}</SelectItem>
              <SelectItem value="1d">{t('dialer:tasks.reschedule.oneDay')}</SelectItem>
              <SelectItem value="1w">{t('dialer:tasks.reschedule.oneWeek')}</SelectItem>
              <SelectItem value="1m">{t('dialer:tasks.reschedule.oneMonth')}</SelectItem>
              <SelectItem value="3m">{t('dialer:tasks.reschedule.threeMonths')}</SelectItem>
              <SelectItem value="6m">{t('dialer:tasks.reschedule.sixMonths')}</SelectItem>
            </SelectContent>
          </Select>
          <Button
            size="sm"
            className="h-7 w-7 p-0 bg-green-600 hover:bg-green-700 text-white"
            onClick={() => onCall(task)}
            title={t('common:actions.call')}
            aria-label={t('common:actions.call')}
          >
            <Phone className="w-3.5 h-3.5" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 w-7 p-0 text-green-600 hover:text-green-700 border-border/60"
            onClick={() => onDone(task)}
            title={t('dialer:tasks.markDone')}
            aria-label={t('dialer:tasks.markDone')}
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Tasks tab ────────────────────────────────────────────────────────────────

function TasksTab({ clientEmail, customerId, onCall, hubspotConnected = false }) {
  const { t } = useTranslation(['pages', 'dialer', 'common']);
  const queryClient = useQueryClient();

  const { data: callbacks = [], isLoading, isError, refetch } = useScheduledCallbacks(clientEmail, true, customerId);

  const pendingTasks = useMemo(
    () => callbacks.filter(c => c.follow_up_at && !c.follow_up_completed),
    [callbacks]
  );

  const groups = useMemo(() => {
    const { now, endOfToday, startOfTomorrow, endOfTomorrow, endOfWeek } = getDateBoundaries();
    return {
      overdue: pendingTasks.filter(c => new Date(c.follow_up_at) < now),
      today: pendingTasks.filter(c => {
        const d = new Date(c.follow_up_at);
        return d >= now && d <= endOfToday;
      }),
      tomorrow: pendingTasks.filter(c => {
        const d = new Date(c.follow_up_at);
        return d >= startOfTomorrow && d <= endOfTomorrow;
      }),
      week: pendingTasks.filter(c => {
        const d = new Date(c.follow_up_at);
        return d > endOfTomorrow && d <= endOfWeek;
      }),
    };
  }, [pendingTasks]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['scheduled-callbacks'] });

  // Reversible (DES-13): applied at once, an undo toast puts it back. A
  // failed write keeps the task as it was and says so (it always said
  // « reporté » before, even when nothing was saved).
  // REV-W4-F7: the undo used to put the task back in XDial only; HubSpot
  // kept the new due date, or the task completed. `hubspotUndo` puts HubSpot
  // back too; null means HubSpot cannot be put back (no route reopens a
  // completed task), so no « Annuler » is offered once HubSpot was written.
  const writeTask = async (task, patch, previous, successKey, hubspotSync, hubspotUndo) => {
    try {
      await api.entities.ParallelCall.update(task.id, patch);
    } catch (err) {
      showErrorToast(err);
      return;
    }
    const syncsHubspot = !!(hubspotConnected && task.hubspot_task_id);
    if (syncsHubspot) hubspotSync?.().catch(() => {});
    refresh();
    if (syncsHubspot && !hubspotUndo) {
      toast.success(t(successKey));
      return;
    }
    toast.success(t(successKey), {
      action: {
        label: t('common:actions.undo'),
        onClick: async () => {
          try {
            await api.entities.ParallelCall.update(task.id, previous);
            if (syncsHubspot) hubspotUndo?.().catch(() => {});
            refresh();
          } catch (err) {
            showErrorToast(err);
          }
        },
      },
    });
  };

  const handleDone = (task) => writeTask(
    task,
    { follow_up_completed: true },
    { follow_up_completed: false },
    'dialer:tasks.toast.done',
    () => api.functions.invoke('completeHubSpotTask', { clientEmail, hubspotTaskId: task.hubspot_task_id }),
    null,
  );

  const handleReschedule = (task, type) => {
    const newDate = rescheduleDate(type);
    if (!newDate) return;
    return writeTask(
      task,
      { follow_up_at: newDate.toISOString() },
      { follow_up_at: task.follow_up_at },
      'dialer:tasks.toast.rescheduled',
      () => api.functions.invoke('updateHubSpotTaskDueDate', { clientEmail, hubspotTaskId: task.hubspot_task_id, dueDate: newDate.toISOString() }),
      () => api.functions.invoke('updateHubSpotTaskDueDate', { clientEmail, hubspotTaskId: task.hubspot_task_id, dueDate: task.follow_up_at }),
    );
  };

  if (isError) {
    return (
      <div role="alert" className="text-center py-12 bg-card rounded-xl border border-border">
        <AlertCircle className="w-8 h-8 text-[var(--vf-status-failed-fg)] mx-auto mb-3" aria-hidden />
        <p className="text-foreground font-medium">{t('dialer:tasks.loadError')}</p>
        <Button size="sm" variant="outline" className="mt-4 min-h-[44px]" onClick={() => refetch()}>
          {t('common:actions.retry')}
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-20 bg-card rounded-xl border border-border animate-pulse" />
        ))}
      </div>
    );
  }

  if (pendingTasks.length === 0) {
    return (
      <div className="text-center py-16 bg-card rounded-xl border border-border">
        <CheckCircle2 className="w-10 h-10 text-green-500/40 mx-auto mb-3" />
        <p className="text-foreground font-medium">{t('dialer:tasks.empty.title')}</p>
        <p className="text-muted-foreground text-sm mt-1">
          {t('dialer:tasks.empty.hint')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Summary bar */}
      <div className="flex items-center gap-2 flex-wrap">
        {groups.overdue.length > 0 && (
          <Badge className="bg-red-100 text-red-700 border-red-200 gap-1">
            <AlertTriangle className="w-3 h-3" />
            {t('dialer:tasks.summary.overdue', { count: groups.overdue.length })}
          </Badge>
        )}
        {groups.today.length > 0 && (
          <Badge className="bg-[var(--vf-accent-bg)] text-[var(--vf-accent-text)] border-[var(--vf-accent-bd)]">
            {t('dialer:tasks.summary.today', { count: groups.today.length })}
          </Badge>
        )}
        {groups.tomorrow.length > 0 && (
          <Badge variant="secondary">{t('dialer:tasks.summary.tomorrowCount', { count: groups.tomorrow.length })}</Badge>
        )}
        {groups.week.length > 0 && (
          <Badge variant="secondary">{t('dialer:tasks.summary.thisWeekCount', { count: groups.week.length })}</Badge>
        )}
      </div>

      {/* Grouped sections */}
      <TaskSection
        label={t('pages:followups.stats.overdue')}
        icon={AlertTriangle}
        iconClass="text-red-500"
        headerClass="bg-red-50"
        tasks={groups.overdue}
        onCall={onCall}
        onDone={handleDone}
        onReschedule={handleReschedule}
        t={t}
      />
      <TaskSection
        label={t('pages:followups.stats.today')}
        icon={Calendar}
        iconClass="text-[var(--vf-accent-text)]"
        headerClass="bg-[var(--vf-accent-bg)]"
        tasks={groups.today}
        onCall={onCall}
        onDone={handleDone}
        onReschedule={handleReschedule}
        t={t}
      />
      <TaskSection
        label={t('pages:followups.stats.tomorrow')}
        icon={ArrowRight}
        iconClass="text-blue-500"
        headerClass=""
        tasks={groups.tomorrow}
        onCall={onCall}
        onDone={handleDone}
        onReschedule={handleReschedule}
        t={t}
      />
      <TaskSection
        label={t('pages:followups.stats.thisWeek')}
        icon={Clock}
        iconClass="text-muted-foreground"
        headerClass=""
        tasks={groups.week}
        onCall={onCall}
        onDone={handleDone}
        onReschedule={handleReschedule}
        t={t}
      />
    </div>
  );
}

// ─── AI Suggestions tab ───────────────────────────────────────────────────────

function AISuggestionsTab({ allCalls, clientEmail }) {
  const { t } = useTranslation(['dialer']);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [error, setError] = useState(null);

  const recentCalls = useMemo(() => {
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000; // last 7 days
    return allCalls
      .filter(c => c.call_status === 'completed' && new Date(c.created_date).getTime() > cutoff)
      .slice(0, 100);
  }, [allCalls]);

  const handleAnalyze = async () => {
    if (recentCalls.length === 0) {
      toast.error(t('dialer:tasks.ai.noRecentCalls'));
      return;
    }
    setIsAnalyzing(true);
    setError(null);
    try {
      // AUDIT-ADM-12: the route reads question and callIds and answers
      // { answer }. The body used to carry calls and client_email (ignored)
      // and the screen read `analysis`, so the panel always failed.
      const resp = await api.functions.invoke('analyzeCampaign', {
        callIds: recentCalls.map(c => c.id).filter(Boolean),
        // Not translated: this is the instruction sent to the analysis
        // backend/LLM, not interface copy the user reads.
        // i18n-exempt: instruction sent to the analysis backend (see above)
        question: "Quelles tâches et actions prioritaires dois-je planifier pour la semaine prochaine basé sur l'historique d'appels ? Liste des prospects à rappeler en priorité et pourquoi.",
      });
      const text = resp?.data?.answer || null;
      if (!text) throw new Error(t('dialer:tasks.ai.analysisEmpty'));
      setSuggestions(text);
    } catch (err) {
      setError(t('dialer:tasks.ai.analysisFailed'));
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Format AI markdown-ish text into JSX
  const formatText = (text) => {
    return text.split('\n').map((line, i) => {
      if (!line.trim()) return <div key={i} className="h-2" />;
      if (line.trim().startsWith('**') && line.trim().endsWith('**')) {
        const content = line.trim().slice(2, -2);
        return <p key={i} className="font-semibold text-foreground mt-3 mb-1">{content}</p>;
      }
      if (line.trim().startsWith('- ') || line.trim().startsWith('• ')) {
        const content = line.trim().slice(2);
        return (
          <div key={i} className="flex gap-2 items-start mb-1">
            <div className="w-1.5 h-1.5 rounded-full bg-[var(--vf-accent)] mt-2 flex-shrink-0" />
            <p className="text-sm text-foreground/85">{content}</p>
          </div>
        );
      }
      if (/^\d+\./.test(line.trim())) {
        const content = line.trim().replace(/^\d+\.\s*/, '');
        const num = line.trim().match(/^(\d+)\./)?.[1];
        return (
          <div key={i} className="flex gap-2 items-start mb-1">
            <span className="text-xs font-bold text-[var(--vf-accent-text)] bg-[var(--vf-accent-bg)] rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">{num}</span>
            <p className="text-sm text-foreground/85">{content}</p>
          </div>
        );
      }
      return <p key={i} className="text-sm text-foreground/80 mb-1">{line}</p>;
    });
  };

  return (
    <div className="space-y-4">
      {/* Context banner */}
      <div className="bg-card rounded-xl border border-border p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-[var(--vf-accent-bg)] flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-[var(--vf-accent-text)]" />
            </div>
            <div>
              <p className="font-medium text-foreground text-sm">{t('dialer:tasks.ai.bannerTitle')}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {recentCalls.length > 0
                  ? t('dialer:tasks.ai.bannerSubtitleWithCalls', { count: recentCalls.length })
                  : t('dialer:tasks.ai.bannerSubtitleEmpty')}
              </p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={handleAnalyze}
            disabled={isAnalyzing || recentCalls.length === 0}
            className="h-8 bg-[var(--vf-primary)] hover:bg-[var(--vf-primary-hover)] flex-shrink-0"
          >
            {isAnalyzing ? (
              <>
                <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                {t('dialer:tasks.ai.analyzing')}
              </>
            ) : (
              <>
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                {t('dialer:tasks.ai.analyzeButton')}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Results */}
      {suggestions && !isAnalyzing && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card rounded-xl border border-border overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-border/50 flex items-center gap-2 bg-[var(--vf-accent-bg)]">
            <Lightbulb className="w-4 h-4 text-[var(--vf-accent-text)]" />
            <span className="text-sm font-semibold text-foreground">{t('dialer:tasks.ai.resultsTitle')}</span>
          </div>
          <div className="px-4 py-4 leading-relaxed">
            {formatText(suggestions)}
          </div>
          <div className="px-4 py-2 border-t border-border/50 flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-muted-foreground"
              onClick={handleAnalyze}
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              {t('dialer:tasks.ai.reanalyze')}
            </Button>
          </div>
        </motion.div>
      )}

      {/* Empty / prompt state */}
      {!suggestions && !isAnalyzing && !error && (
        <div className="text-center py-12 bg-card rounded-xl border border-dashed border-border">
          <TrendingUp className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-foreground font-medium text-sm">{t('dialer:tasks.ai.noAnalysisTitle')}</p>
          <p className="text-muted-foreground text-xs mt-1 max-w-xs mx-auto">
            {t('dialer:tasks.ai.noAnalysisHint', { button: t('dialer:tasks.ai.analyzeButton') })}
          </p>
          {recentCalls.length === 0 && (
            <p className="text-amber-600 text-xs mt-3 flex items-center justify-center gap-1">
              <Target className="w-3.5 h-3.5" />
              {t('dialer:tasks.ai.needOneCall')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Meetings of the Agenda tab (the former Booking tab, merged in W4) ─────────

function AgendaMeetings({ clientEmail, customerId }) {
  const { t } = useTranslation(['dialer']);
  // User-level reminder preferences, persisted in Caller.dialer_settings.
  const { remindersEnabled, setRemindersEnabled, reminderChannels, setReminderChannels } = useDialer();
  const reminderFlags = reminderChannels || {};
  const toggleFlag = (key) => {
    setReminderChannels({ ...reminderFlags, [key]: !reminderFlags[key] });
  };

  const { data: meetings = [], isLoading } = useQuery({
    queryKey: ['client-meetings', clientEmail, customerId ?? null],
    queryFn: async () => {
      if (!clientEmail) return [];
      const result = await api.entities.ClientMeeting.filter(customerFilter(customerId));
      return Array.isArray(result) ? result : (result?.data ?? []);
    },
    enabled: !!clientEmail,
  });

  const stats = React.useMemo(() => {
    const now = Date.now();
    const upcoming  = meetings.filter((m) => meetingDate(m) && new Date(meetingDate(m)).getTime() > now).length;
    const confirmed = meetings.filter((m) => m.status === 'confirmed').length;
    const pending   = meetings.filter((m) => m.status === 'pending').length;
    const noShow    = meetings.filter((m) => m.status === 'no_show').length;
    const declined  = meetings.filter((m) => m.status === 'declined').length;
    return { upcoming, confirmed, pending, noShow, declined };
  }, [meetings]);

  // À traiter : meetings en pending dans les 48h + no_shows récents
  const toProcess = meetings.filter((m) => m.status === 'pending' || m.status === 'no_show').slice(0, 10);


  return (
    <div className="space-y-5 py-4">
      <h2 className="text-[15px] font-semibold" style={{ color: 'var(--vf-text-1)' }}>{t('dialer:tasks.meetingsSection')}</h2>
      {/* 5 stats tuiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {[
          { label: t('dialer:tasks.bookingTab.stats.upcoming'), value: stats.upcoming,  color: 'var(--vf-accent)' },
          { label: t('dialer:tasks.confirmed'), value: stats.confirmed, color: 'var(--vf-green)' },
          { label: t('dialer:tasks.pendingTasks'), value: stats.pending,  color: 'var(--vf-orange)' },
          { label: t('dialer:tasks.bookingTab.stats.noShows'),  value: stats.noShow,    color: 'var(--vf-red)' },
          { label: t('dialer:tasks.bookingTab.stats.declined'),  value: stats.declined,  color: 'var(--vf-red)' },
        ].map((s) => (
          <div
            key={s.label}
            className="rounded-vf-lg border p-4 text-center"
            style={{ background: 'var(--vf-surface-2)', borderColor: 'var(--vf-border)' }}
          >
            <div className="vf-font-mono text-[28px] font-semibold" style={{ color: s.color }}>
              {isLoading ? '—' : s.value}
            </div>
            <div className="text-[11px] font-medium uppercase tracking-wider mt-1" style={{ color: 'var(--vf-text-3)' }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>

      {/* À traiter */}
      <div>
        <h3 className="text-[15px] font-semibold mb-2" style={{ color: 'var(--vf-text-1)' }}>{t('dialer:tasks.bookingTab.toProcessTitle')}</h3>
        {toProcess.length === 0 ? (
          <div
            className="rounded-vf-lg border p-6 text-center text-sm"
            style={{ background: 'var(--vf-surface-2)', borderColor: 'var(--vf-border)', color: 'var(--vf-text-3)' }}
          >
            {t('dialer:tasks.bookingTab.noneToProcess')}
          </div>
        ) : (
          <div className="space-y-2">
            {toProcess.map((m) => (
              <div
                key={m.id}
                className="flex items-center gap-3 rounded-vf-lg border p-3"
                style={{ background: 'var(--vf-surface-2)', borderColor: 'var(--vf-border)' }}
              >
                <span
                  className="h-2 w-2 rounded-full shrink-0"
                  style={{ background: m.status === 'no_show' ? 'var(--vf-red)' : 'var(--vf-orange)' }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold truncate" style={{ color: 'var(--vf-text-1)' }}>
                    {m.prospect_name || m.prospectName || t('dialer:tasks.bookingTab.prospectFallback')}
                    {(m.prospect_company || m.companyName) ? ` · ${m.prospect_company || m.companyName}` : ''}
                  </div>
                  <div className="text-[11px]" style={{ color: 'var(--vf-text-3)' }}>
                    {m.status === 'no_show' ? t('dialer:tasks.bookingTab.noShowReplan') : t('dialer:tasks.bookingTab.invitationPending')}
                    {meetingDate(m) ? ` · ${formatWeekdayDay(meetingDate(m))}` : ''}
                  </div>
                </div>
                <button
                  className="rounded-vf px-3 h-8 text-xs font-medium"
                  style={{ background: 'var(--vf-surface-3)', color: 'var(--vf-text-1)', border: '1px solid var(--vf-border)' }}
                >
                  {m.status === 'no_show' ? t('dialer:tasks.bookingTab.replan') : t('dialer:tasks.bookingTab.followUp')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Rappels automatiques (GAP-3 backend — voir commentaire ci-dessus) */}
      <div
        className="rounded-vf-lg border p-4"
        style={{ background: 'var(--vf-surface-2)', borderColor: 'var(--vf-border)' }}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[14px] font-semibold" style={{ color: 'var(--vf-text-1)' }}>{t('dialer:tasks.bookingTab.remindersTitle')}</h3>
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={remindersEnabled}
              onChange={(e) => setRemindersEnabled(e.target.checked)}
              className="sr-only"
            />
            <span
              className="relative inline-block transition-colors"
              style={{
                width: 36, height: 20, borderRadius: 10,
                background: remindersEnabled ? 'var(--vf-accent)' : 'var(--vf-surface-4)',
              }}
            >
              <span
                className="absolute top-0.5 transition-all"
                style={{
                  width: 16, height: 16, borderRadius: '50%', background: 'white',
                  left: remindersEnabled ? 18 : 2,
                }}
              />
            </span>
          </label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {[
            { key: 'emailJ1', label: t('dialer:tasks.bookingTab.reminderLabels.emailJ1') },
            { key: 'emailH1', label: t('dialer:tasks.bookingTab.reminderLabels.emailH1') },
            { key: 'smsJ1',   label: t('dialer:tasks.bookingTab.reminderLabels.smsJ1') },
            { key: 'smsH1',   label: t('dialer:tasks.bookingTab.reminderLabels.smsH1') },
          ].map((r) => (
            <label key={r.key} className="flex items-center gap-2 cursor-pointer" style={{ opacity: remindersEnabled ? 1 : 0.4 }}>
              <input
                type="checkbox"
                checked={!!reminderFlags[r.key]}
                onChange={() => remindersEnabled && toggleFlag(r.key)}
                disabled={!remindersEnabled}
                className="rounded"
                style={{ accentColor: 'var(--vf-accent)' }}
              />
              <span className="text-[13px]" style={{ color: 'var(--vf-text-1)' }}>{r.label}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function FollowupsScreen() {
  const { t } = useTranslation(['dialer', 'common']);
  // ADR-024 — Customer-scoped followups.
  const { callerData, filteredAllCalls: allCalls, handleCallProspect, selectedCustomerId } = useDialer();
  const [activeTab, setActiveTab] = useTabParam(TAB_IDS, 'agenda');
  // Same query as the menu badge and ⌘K: the tab shows the same number.
  const { data: callbacks = [] } = useScheduledCallbacks(callerData?.client_email, true, selectedCustomerId);
  const overdueCount = countOverdueFollowups(callbacks, Date.now());

  const clientEmail = callerData?.client_email;

  // Check HubSpot connection status — only show badge when truly connected
  const { data: hubspotStatus } = useQuery({
    queryKey: ['hubspot-sync-status', clientEmail || '_self'],
    queryFn: async () => {
      const result = await api.functions.invoke('getHubSpotSyncStatus', { clientEmail });
      return result.data;
    },
    refetchInterval: 60000,
    retry: 1,
    // Silently suppress errors — absence of badge is correct when disconnected
    throwOnError: false,
  });

  const hubspotConnected = hubspotStatus?.connected === true;

  const handleCall = (task) => {
    handleCallProspect({
      id: task.id,
      prospect_name: task.prospect_name,
      prospect_phone: task.prospect_phone,
      prospect_company: task.prospect_company,
    });
  };

  // GAP-7 : Alerte urgentes — invitations non confirmées dans les prochaines 24h + no-shows
  const { data: meetings = [] } = useQuery({
    queryKey: ['client-meetings-urgent', clientEmail, selectedCustomerId ?? null],
    queryFn: async () => {
      if (!clientEmail) return [];
      const result = await api.entities.ClientMeeting.filter(customerFilter(selectedCustomerId));
      return Array.isArray(result) ? result : (result?.data ?? []);
    },
    enabled: !!clientEmail,
    refetchInterval: 120000,
  });

  // GAP-6 : Actions IA suggérées (badge count sur l'onglet)
  const { data: suggestedActions = [] } = useQuery({
    queryKey: ['ai-suggested-actions', clientEmail, callerData?.id],
    queryFn: async () => {
      if (!clientEmail) return [];
      try {
        const result = await api.functions.invoke('getSuggestedActions', {
          clientEmail,
          callerId: callerData?.id,
        });
        return result?.data || [];
      } catch { return []; }
    },
    enabled: !!clientEmail,
    refetchInterval: 300000, // 5 min
  });

  const urgentCount = React.useMemo(() => {
    const now = Date.now();
    const in24h = now + 24 * 3600 * 1000;
    const pendingUpcoming = meetings.filter((m) => {
      if (m.status !== 'pending' || !meetingDate(m)) return false;
      const ts = new Date(meetingDate(m)).getTime();
      return ts > now && ts < in24h;
    }).length;
    const noShows = meetings.filter((m) => {
      if (m.status !== 'pending' || !meetingDate(m)) return false;
      return new Date(meetingDate(m)).getTime() < now - 15 * 60 * 1000;
    }).length;
    return pendingUpcoming + noShows;
  }, [meetings]);

  return (
    <motion.div
      key="followups"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-4"
    >
      {/* GAP-7 : Alerte actions urgentes */}
      {urgentCount > 0 && (
        <div
          className="flex items-center gap-3 rounded-vf-lg border-l-4 px-4 py-3"
          style={{
            background: 'var(--vf-red-bg)',
            borderLeftColor: 'var(--vf-red)',
            color: 'var(--vf-text-1)',
          }}
        >
          <AlertCircle size={16} style={{ color: 'var(--vf-red)' }} />
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold" style={{ color: 'var(--vf-red)' }}>
              {t('dialer:tasks.urgent.count', { count: urgentCount })}
            </div>
            <div className="text-[11px]" style={{ color: 'var(--vf-text-2)' }}>
              {t('dialer:tasks.urgent.subtitle')}
            </div>
          </div>
          <button
            onClick={() => setActiveTab('agenda')}
            className="rounded-vf px-3 h-8 text-[12px] font-medium"
            style={{ background: 'var(--vf-red)', color: 'white' }}
          >
            {t('dialer:tasks.urgent.view')}
          </button>
        </div>
      )}

      {/* HubSpot sync status indicator: shown only when connected, and never
          while HubSpot is hidden from the UI (AUDIT-INT-08, src/lib/hubspotUi.ts).
          The task sync itself (complete, due date) keeps running for a
          customer already on HubSpot. */}
      {HUBSPOT_UI_ENABLED && hubspotConnected && (
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-orange-50 border border-orange-200 text-orange-700 text-xs font-medium">
            <Upload className="w-3 h-3" />
            <span>HubSpot</span>
            <CheckCircle2 className="w-3 h-3 text-green-600" />
          </div>
        </div>
      )}

      {/* Tab bar — VF underline style */}
      <div
        className="flex items-end gap-1 border-b"
        style={{ borderColor: 'var(--vf-border)' }}
      >
        {TABS.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          const count =
            tab.id === 'agenda' ? (meetings || []).filter((m) => meetingDate(m) && new Date(meetingDate(m)).toDateString() === new Date().toDateString()).length :
            tab.id === 'tasks'  ? overdueCount :
            tab.id === 'ai'     ? suggestedActions.length :
            null;
          return (
            <button
              key={tab.id}
              aria-current={isActive ? 'true' : undefined}
              onClick={() => setActiveTab(tab.id)}
              className="relative -mb-px inline-flex items-center gap-1.5 px-3 h-9 text-[13px] font-medium transition-colors"
              style={{
                color: isActive ? 'var(--vf-text-1)' : 'var(--vf-text-3)',
              }}
              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = 'var(--vf-text-1)'; }}
              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = 'var(--vf-text-3)'; }}
            >
              <Icon size={14} />
              <span>{t(`dialer:tasks.${tab.labelKey}`)}</span>
              {count !== null && count > 0 && (
                <span
                  className="inline-flex min-w-[18px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold"
                  style={{
                    height: 18,
                    background: isActive ? 'var(--vf-accent)' : 'var(--vf-surface-3)',
                    color: isActive ? 'white' : 'var(--vf-text-2)',
                  }}
                >
                  {count}
                </span>
              )}
              {isActive && (
                <span
                  className="absolute inset-x-0 -bottom-px h-0.5 rounded-full"
                  style={{ background: 'var(--vf-accent)' }}
                />
              )}
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-2 pb-2">
          <button
            onClick={() => setActiveTab('tasks')}
            className="inline-flex items-center gap-1 rounded-vf px-2.5 h-8 text-xs font-medium"
            style={{
              background: 'var(--vf-surface-2)',
              border: '1px solid var(--vf-border)',
              color: 'var(--vf-text-1)',
            }}
          >
            {t('dialer:tasks.addTask')}
          </button>
          {/* « + RDV » records a meeting, as « Nouveau RDV » everywhere else
              (it only switched to the Booking tab). */}
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('revcall:new-meeting'))}
            className="inline-flex items-center gap-1 rounded-vf px-2.5 h-8 text-xs font-medium text-white"
            style={{ background: 'var(--vf-primary)' }}
          >
            {t('dialer:tasks.addMeeting')}
          </button>
        </div>
      </div>

      {/* Tab content */}
      <AnimatePresence mode="wait">
        {activeTab === 'agenda' && (
          <motion.div
            key="agenda"
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 8 }}
            transition={{ duration: 0.15 }}
          >
            <TaskAgendaVF
              clientEmail={callerData?.client_email}
              customerId={selectedCustomerId}
              onCall={handleCall}
            />
            <AgendaMeetings clientEmail={callerData?.client_email} customerId={selectedCustomerId} />
          </motion.div>
        )}

        {activeTab === 'tasks' && (
          <motion.div
            key="tasks"
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 8 }}
            transition={{ duration: 0.15 }}
          >
            <TasksTab
              clientEmail={callerData?.client_email}
              customerId={selectedCustomerId}
              onCall={handleCall}
              hubspotConnected={hubspotConnected}
            />
          </motion.div>
        )}

        {activeTab === 'ai' && (
          <motion.div
            key="ai"
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 8 }}
            transition={{ duration: 0.15 }}
          >
            <AISuggestionsTab
              allCalls={allCalls}
              clientEmail={callerData?.client_email}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
